import { useLocation } from "@tanstack/react-router";
import { useFeature } from "flagged";
import type { MRT_ColumnFiltersState, MRT_SortingState } from "mantine-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTab } from "src/contexts/tabContext";
import { TourProvider, useTour } from "src/contexts/tourContext";
import type { ChatVectorIndexShorthand } from "src/db";
import { documentViewerTourSteps } from "./documentViewerTourSteps";
import { GuidedTour } from "./GuidedTour";
import ICMDocumentModal from "./ICMDocumentModal";
import { columnsMap } from "./MetadataDocuments/columns";
import { useMetadataDocumentsQuery } from "./MetadataDocuments/hooks/useMetadataDocumentsQuery";
import { MetadataDocumentsTable } from "./MetadataDocuments/MetadataDocumentsTable";
import type { ICMDocument } from "./MetadataDocuments/types";
import { flattenMetadataHits } from "./MetadataDocuments/utils";
import { PdfFocusProvider } from "./PDFViewer/context";

interface SearchHitsTotal {
    value: number;
    relation: string;
}

interface SearchResponsePage {
    hits: {
        total: SearchHitsTotal;
        hits: unknown[];
    };
}

interface QueryData {
    pages?: SearchResponsePage[];
}

const MetadataDocuments = ({
    index,
    onFilteredCountChange,
}: {
    index: ChatVectorIndexShorthand;
    onFilteredCountChange?: (count: number) => void;
}) => {
    const showDocViewerMarkdown = useFeature("SHOW_DOCUMENT_VIEWER_MARKDOWN");
    const columns = useMemo(() => columnsMap[index] ?? [], [index]);
    const location = useLocation();
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>();

    // State for ICM document modal
    const [selectedICMDocument, setSelectedICMDocument] = useState<ICMDocument | null>(null);

    // Track previous index to reset filters when it changes
    const prevIndexRef = useRef<ChatVectorIndexShorthand | undefined>(undefined);
    useEffect(() => {
        if (prevIndexRef.current !== undefined && prevIndexRef.current !== index) {
            // Reset all filters when the index prop changes
            setColumnFilters([]);
            setGlobalFilter(undefined);
            setSorting([]);
        }
        prevIndexRef.current = index;
    }, [index]);

    const { data, fetchNextPage, isFetching, isLoading } = useMetadataDocumentsQuery({
        index,
        sorting,
        columnFilters,
        globalFilter,
    });

    const flatData = useMemo(() => flattenMetadataHits(data, index), [data, index]);

    const filteredCount = useMemo(() => {
        const typedData = data as QueryData;
        return typedData?.pages?.[0]?.hits?.total?.value ?? 0;
    }, [data]);

    const hasActiveFilters = useMemo(() => {
        return columnFilters.length > 0 || globalFilter !== undefined;
    }, [columnFilters, globalFilter]);

    useEffect(() => {
        if (onFilteredCountChange) {
            onFilteredCountChange(hasActiveFilters ? filteredCount : undefined);
        }
    }, [filteredCount, onFilteredCountChange, hasActiveFilters]);

    const { setSelectedTab } = useTab();

    useEffect(() => {
        setSelectedTab("field_service_reports");
    }, [setSelectedTab]);

    function TourConsumer() {
        const { isTourOpen, completeTour } = useTour();
        return (
            <GuidedTour
                steps={documentViewerTourSteps}
                onComplete={completeTour}
                onClose={completeTour}
                isOpen={isTourOpen}
                pathCheck={() => location.pathname.includes("/documents")}
            />
        );
    }
    return (
        <TourProvider tourId="docs-tour">
            <TourConsumer />
            <PdfFocusProvider>
                <MetadataDocumentsTable
                    index={index}
                    columns={columns}
                    data={flatData}
                    queryData={data}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    sorting={sorting}
                    setSorting={setSorting}
                    columnFilters={columnFilters}
                    setColumnFilters={setColumnFilters}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    fetchNextPage={fetchNextPage}
                    onICMDocumentSelect={
                        index === "icm" && showDocViewerMarkdown
                            ? setSelectedICMDocument
                            : undefined
                    }
                />
                {index === "icm" && showDocViewerMarkdown && (
                    <ICMDocumentModal
                        document={selectedICMDocument}
                        onClose={() => setSelectedICMDocument(null)}
                    />
                )}
            </PdfFocusProvider>
        </TourProvider>
    );
};

export default MetadataDocuments;
